'use client';

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Download, FileSpreadsheet, Plus, RefreshCw, Save, Search, Sparkles, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEquaFlowStore } from '@/lib/store';
import { detectFieldRole, isFormula, requiresExcelEvaluation } from '@/lib/formula';
import { getExampleDSL } from '@/lib/dsl-parser';
import { importSpreadsheetFile, MAX_IMPORT_ROWS_PER_SHEET, SUPPORTED_SPREADSHEET_EXTENSIONS, type SpreadsheetImportResult } from '@/lib/spreadsheet-import';
import { createNormalizationManifest, type SpreadsheetNormalizationPlan } from '@/lib/spreadsheet-normalization';
import { toast } from 'sonner';

const UNITS = ['none', '$', '€', '₽', '£', '%', 'шт', 'time'] as const;
type TableFilter = 'all' | 'input' | 'formula' | 'output' | 'attention';

function shownValue(value: string | number | null, status: 'input' | 'output'): string {
  if (value === null) return '';
  const raw = String(value);
  return isFormula(raw) && status === 'input' && !raw.startsWith('=') ? `=${raw}` : raw;
}

function toModelValue(value: string): string | number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const formula = trimmed.startsWith('=') ? trimmed.slice(1).trim() : trimmed;
  if (!formula) return null;
  const numeric = Number(formula);
  const looksLikeTextCode = /^[-+]?0\d+$/.test(formula) || /^\+\d/.test(formula);
  return Number.isFinite(numeric) && !/[A-Za-z_]/.test(formula) && !looksLikeTextCode ? numeric : formula;
}

function roleStyle(role: 'input' | 'formula' | 'output'): string {
  if (role === 'formula') return 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800';
  if (role === 'output') return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800';
  return 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
}

export function SpreadsheetView() {
  const entities = useEquaFlowStore((state) => state.entities);
  const currentProjectName = useEquaFlowStore((state) => state.currentProjectName);
  const showSolution = useEquaFlowStore((state) => state.showSolution);
  const solveStatus = useEquaFlowStore((state) => state.solveStatus);
  const timeHorizon = useEquaFlowStore((state) => state.timeHorizon);
  const updateField = useEquaFlowStore((state) => state.updateField);
  const addField = useEquaFlowStore((state) => state.addField);
  const addEntity = useEquaFlowStore((state) => state.addEntity);
  const removeEntity = useEquaFlowStore((state) => state.removeEntity);
  const removeField = useEquaFlowStore((state) => state.removeField);
  const importProject = useEquaFlowStore((state) => state.importProject);
  const importFromDSL = useEquaFlowStore((state) => state.importFromDSL);
  const setCurrentProject = useEquaFlowStore((state) => state.setCurrentProject);
  const recalculateExcelFormulas = useEquaFlowStore((state) => state.recalculateExcelFormulas);
  const autoSave = useEquaFlowStore((state) => state.autoSave);
  const [isImporting, setIsImporting] = useState(false);
  const [pendingImport, setPendingImport] = useState<SpreadsheetImportResult | null>(null);
  const [pendingFileName, setPendingFileName] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [normalizationPlan, setNormalizationPlan] = useState<SpreadsheetNormalizationPlan | null>(null);
  const [isNormalizing, setIsNormalizing] = useState(false);
  const [isOfficeRecalculating, setIsOfficeRecalculating] = useState(false);
  const [selectedSheetNames, setSelectedSheetNames] = useState<string[]>([]);
  const [textEncoding, setTextEncoding] = useState<'utf-8' | 'windows-1251'>('utf-8');
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [tableQuery, setTableQuery] = useState('');
  const [tableFilter, setTableFilter] = useState<TableFilter>('all');
  const [saveState, setSaveState] = useState<'saving' | 'saved'>('saving');
  const [collapsedEntityIds, setCollapsedEntityIds] = useState<Set<string>>(() => new Set());
  const [expandedFormulaId, setExpandedFormulaId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const entityList = useMemo(() => Object.values(entities), [entities]);
  const formulaCount = useMemo(
    () => entityList.flatMap((entity) => entity.fields).filter((field) => detectFieldRole(field.status, field.value) === 'formula').length,
    [entityList],
  );

  const visibleEntityList = useMemo(() => {
    const query = tableQuery.trim().toLowerCase();
    return entityList.map((entity) => {
      const entityMatches = !query || entity.entityType.toLowerCase().includes(query);
      const fields = entity.fields.filter((field) => {
        const role = detectFieldRole(field.status, field.value);
        const textMatches = entityMatches || !query || `${field.name} ${field.value ?? ''}`.toLowerCase().includes(query);
        const matchesFilter = tableFilter === 'all'
          || role === tableFilter
          || (tableFilter === 'attention' && (field.isValid === false || (role === 'formula' && requiresExcelEvaluation(String(field.value)))));
        return textMatches && matchesFilter;
      });
      return { ...entity, fields };
    }).filter((entity) => entity.fields.length > 0);
  }, [entityList, tableFilter, tableQuery]);

  const visibleFieldCount = useMemo(
    () => visibleEntityList.reduce((count, entity) => count + entity.fields.length, 0),
    [visibleEntityList],
  );

  const hasSelectedOversizedSheet = useMemo(
    () => pendingImport?.source === 'spreadsheet' && Boolean(
      pendingImport.preview?.sheets.some((sheet) =>
        selectedSheetNames.includes(sheet.name) && sheet.importableRows > MAX_IMPORT_ROWS_PER_SHEET,
      ),
    ),
    [pendingImport, selectedSheetNames],
  );

  const saveNow = useCallback(() => {
    setSaveState('saving');
    autoSave();
    setSaveState('saved');
  }, [autoSave]);

  useEffect(() => {
    if (entityList.length === 0) return;
    setSaveState('saving');
    const timer = window.setTimeout(saveNow, 350);
    return () => window.clearTimeout(timer);
  }, [entityList, saveNow]);

  const commitValue = useCallback((entityId: string, fieldId: string, value: string) => {
    const next = toModelValue(value);
    updateField(entityId, fieldId, { value: next, status: next === null ? 'output' : 'input', blockedExcelFormula: undefined });
  }, [updateField]);

  const focusValueCell = useCallback((current: HTMLInputElement, direction = 1): boolean => {
    const cells = Array.from(document.querySelectorAll<HTMLInputElement>('[data-spreadsheet-value-cell]'));
    const next = cells[cells.indexOf(current) + direction];
    if (!next) return false;
    window.requestAnimationFrame(() => next.focus());
    return true;
  }, []);

  const prepareImport = useCallback(async (file: File) => {
    setIsImporting(true);
    setNormalizationPlan(null);
    try {
      const result = await importSpreadsheetFile(file, { previewOnly: true });
      setPendingFileName(file.name);
      setPendingFile(file);
      setSelectedSheetNames(result.preview?.sheets.map((sheet) => sheet.name) ?? []);
      setTextEncoding(result.textEncoding === 'windows-1251' ? 'windows-1251' : 'utf-8');
      setPendingImport(result);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not import this spreadsheet.');
    } finally {
      setIsImporting(false);
    }
  }, []);

  const handleImport = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) void prepareImport(file);
  }, [prepareImport]);

  const changeTextEncoding = useCallback(async (nextEncoding: 'utf-8' | 'windows-1251') => {
    setTextEncoding(nextEncoding);
    setNormalizationPlan(null);
    if (!pendingFile) return;
    setIsImporting(true);
    try {
      const result = await importSpreadsheetFile(pendingFile, { textEncoding: nextEncoding, previewOnly: true });
      setPendingImport(result);
      setSelectedSheetNames((names) => names.filter((name) => result.preview?.sheets.some((sheet) => sheet.name === name)));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not preview this encoding.');
    } finally {
      setIsImporting(false);
    }
  }, [pendingFile]);

  const requestNormalization = useCallback(async () => {
    if (!pendingImport?.preview?.schema || !pendingFile) return;
    setIsNormalizing(true);
    try {
      const response = await fetch('/api/v1/spreadsheet-normalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manifest: createNormalizationManifest(pendingImport.preview.schema) }),
      });
      const data = await response.json() as { error?: string; plan?: SpreadsheetNormalizationPlan };
      if (!response.ok || !data.plan) throw new Error(data.error || 'The normalization agent did not return a plan.');

      const result = await importSpreadsheetFile(pendingFile, {
        previewOnly: true,
        sheetNames: selectedSheetNames,
        textEncoding,
        normalizationPlan: data.plan,
      });
      setPendingImport(result);
      setSelectedSheetNames((names) => names.filter((name) => result.preview?.sheets.some((sheet) => sheet.name === name)));
      setNormalizationPlan(data.plan);
      toast.success('A safe structural plan was applied locally. Review it before importing.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not normalize this spreadsheet.');
    } finally {
      setIsNormalizing(false);
    }
  }, [pendingFile, pendingImport, selectedSheetNames, textEncoding]);

  const requestOfficeRecalculation = useCallback(async () => {
    if (!pendingFile) return;
    setIsOfficeRecalculating(true);
    try {
      const form = new FormData();
      form.append('file', pendingFile);
      const response = await fetch('/api/v1/spreadsheet-recalculate', { method: 'POST', body: form });
      if (!response.ok) {
        const data = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error || 'Office could not recalculate this workbook.');
      }
      const baseName = pendingFile.name.replace(/\.[^.]+$/, '') || 'workbook';
      const recalculated = new File(
        [await response.blob()],
        `${baseName}-recalculated.xlsx`,
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      );
      await prepareImport(recalculated);
      toast.success('Office recalculated the workbook in an isolated temporary workspace. Review the refreshed preview.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not recalculate this workbook with Office.');
    } finally {
      setIsOfficeRecalculating(false);
    }
  }, [pendingFile, prepareImport]);

  const confirmImport = useCallback(async () => {
    if (!pendingImport || !pendingFile) return;
    if (pendingImport.source === 'spreadsheet' && selectedSheetNames.length === 0) {
      toast.error('Select at least one sheet to import.');
      return;
    }
    setIsImporting(true);
    try {
      const result = pendingImport.source === 'equaflow'
        ? pendingImport
        : await importSpreadsheetFile(pendingFile, { sheetNames: selectedSheetNames, textEncoding, normalizationPlan: normalizationPlan ?? undefined });
      if (!importProject(JSON.stringify(result.project))) throw new Error('EquaFlow could not load this spreadsheet.');
      toast.success(result.source === 'equaflow'
        ? 'EquaFlow model restored exactly.'
        : `Imported ${result.project.entities.length} spreadsheet rows.`);
      if (result.source === 'spreadsheet') {
        toast.info('Imported data are saved only in this browser profile. Use a private profile or clear local projects on shared devices.');
      }
      result.warnings.forEach((warning) => toast.warning(warning));
      setPendingImport(null);
      setPendingFile(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not import this spreadsheet.');
    } finally {
      setIsImporting(false);
    }
  }, [importProject, normalizationPlan, pendingFile, pendingImport, selectedSheetNames, textEncoding]);

  const handleExport = useCallback(() => {
    void import('@/lib/export-xlsx')
      .then(({ downloadXLSX }) => downloadXLSX(entities, currentProjectName, showSolution, solveStatus, timeHorizon))
      .catch(() => toast.error('Could not export this workbook. Please try again or reduce the model size.'));
  }, [currentProjectName, entities, showSolution, solveStatus, timeHorizon]);

  const handleRecalculate = useCallback(() => {
    recalculateExcelFormulas();
    toast.success('Compatible Excel formulas recalculated. Native-only formulas remain ready for Excel export.');
  }, [recalculateExcelFormulas]);

  const loadStarterTemplate = useCallback(() => {
    if (entityList.length > 0 && !window.confirm('Replace the current model with the starter template?')) return;
    const result = importFromDSL(getExampleDSL());
    if (result.errors.length > 0) {
      toast.error('The starter template could not be parsed.');
      return;
    }
    setCurrentProject(null, 'Starter model');
    toast.success('Starter template loaded.');
  }, [entityList.length, importFromDSL, setCurrentProject]);

  return (
    <section
      className="h-full min-h-0 bg-[radial-gradient(circle_at_10%_0%,rgba(59,130,246,0.09),transparent_32%),radial-gradient(circle_at_90%_10%,rgba(16,185,129,0.08),transparent_30%)] p-3 sm:p-5"
      onDragOver={(event) => { event.preventDefault(); setIsDropTarget(true); }}
      onDragLeave={() => setIsDropTarget(false)}
      onDrop={(event) => {
        event.preventDefault();
        setIsDropTarget(false);
        const file = event.dataTransfer.files?.[0];
        if (file) void prepareImport(file);
      }}
    >
      <span role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {isImporting ? 'Preparing spreadsheet import.' : pendingImport ? 'Spreadsheet import preview is ready.' : ''}
      </span>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={SUPPORTED_SPREADSHEET_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
        onChange={handleImport}
      />

      <div className={`relative mx-auto flex h-full max-w-[1680px] min-h-0 flex-col overflow-hidden rounded-2xl border bg-card shadow-2xl shadow-slate-900/5 transition-colors dark:shadow-black/20 ${isDropTarget ? 'border-emerald-500 ring-4 ring-emerald-500/15' : 'border-border'}`}>
        <div className="flex flex-wrap items-center gap-3 border-b border-border bg-gradient-to-r from-slate-50 via-white to-emerald-50/50 px-4 py-3 dark:from-slate-950 dark:via-card dark:to-emerald-950/10">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-white shadow-sm">
              <FileSpreadsheet className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold">Excel workspace</h2>
              <p className="text-[11px] text-muted-foreground">{entityList.length} model blocks · {formulaCount} formulas · one model shared with the visual view</p>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <span className="hidden rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] font-medium text-sky-700 sm:inline dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-300" title="Compatible Excel formulas are recalculated in the browser. Native formulas and their source sheet are retained for XLSX export.">
              Excel formulas calculate
            </span>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={loadStarterTemplate}>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Template
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => fileInputRef.current?.click()} disabled={isImporting}>
              <Upload className="mr-1.5 h-3.5 w-3.5" />
              {isImporting ? 'Importing…' : 'Import Excel / CSV'}
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleExport} disabled={entityList.length === 0}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Export XLSX
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleRecalculate} disabled={entityList.length === 0} title="Recalculate compatible Excel formulas in this browser">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Recalculate
            </Button>
            <Button size="sm" className="h-8 text-xs" onClick={() => addEntity('New_row')}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add row
            </Button>
          </div>
        </div>

        {currentProjectName === 'Starter model' && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-violet-200/70 bg-violet-50/60 px-4 py-2 text-[11px] text-violet-900 dark:border-violet-900/60 dark:bg-violet-950/20 dark:text-violet-100">
            <span className="font-semibold">Starter model</span>
            <span className="text-violet-800/75 dark:text-violet-200/75">Change any value safely; use Template to restore this parseable example.</span>
            <span className="rounded bg-white/70 px-1.5 py-0.5 dark:bg-black/20">input = editable</span>
            <span className="rounded bg-white/70 px-1.5 py-0.5 dark:bg-black/20">formula = calculated</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/15 px-4 py-2">
          <label className="sr-only" htmlFor="excel-table-search">Search table</label>
          <div className="relative min-w-[210px] flex-1 sm:max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              id="excel-table-search"
              value={tableQuery}
              onChange={(event) => setTableQuery(event.target.value)}
              placeholder="Search entity, field, value or formula"
              className="h-8 w-full rounded-md border border-border bg-background py-1 pl-8 pr-2 text-xs outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
            />
          </div>
          <label className="sr-only" htmlFor="excel-table-filter">Filter rows</label>
          <select id="excel-table-filter" value={tableFilter} onChange={(event) => setTableFilter(event.target.value as TableFilter)} className="h-8 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-primary">
            <option value="all">All rows</option>
            <option value="input">Inputs</option>
            <option value="formula">Formulas</option>
            <option value="output">Outputs</option>
            <option value="attention">Needs attention</option>
          </select>
          <span className="text-[11px] text-muted-foreground">{visibleFieldCount} fields in {visibleEntityList.length} blocks</span>
          <button type="button" onClick={saveNow} className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 text-[11px] font-medium text-emerald-800 transition-colors hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200" title="Saved in this browser. Export XLSX to download a copy.">
            <Save className="h-3.5 w-3.5" /> {saveState === 'saving' ? 'Saving locally…' : 'Saved locally'}
          </button>
          <span role="status" aria-live="polite" aria-atomic="true" className="sr-only">
            {saveState === 'saving' ? 'Saving locally.' : 'Saved locally.'}
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-auto overscroll-contain bg-[linear-gradient(to_right,transparent_0,transparent_99%,rgba(148,163,184,0.08)_100%)]">
          <table className="min-w-[860px] w-full border-separate border-spacing-0 text-left text-xs">
            <thead className="sticky top-0 z-20 bg-slate-50/95 shadow-sm backdrop-blur dark:bg-slate-950/95">
              <tr className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                <th className="sticky left-0 z-30 w-52 border-b border-r border-border bg-slate-50 px-3 py-2.5 font-semibold dark:bg-slate-950">Entity</th>
                <th className="sticky left-52 z-30 w-44 border-b border-r border-border bg-slate-50 px-3 py-2.5 font-semibold dark:bg-slate-950">Field</th>
                <th className="min-w-[250px] border-b border-r border-border px-3 py-2.5 font-semibold">Value or formula</th>
                <th className="w-28 border-b border-r border-border px-3 py-2.5 font-semibold">Kind</th>
                <th className="w-24 border-b border-r border-border px-3 py-2.5 font-semibold">Unit</th>
                <th className="w-40 border-b border-border px-3 py-2.5 font-semibold">Result</th>
              </tr>
            </thead>
            <tbody>
              {visibleEntityList.map((entity) => (
                <Fragment key={entity.id}>
                  <tr className="bg-slate-50/70 dark:bg-slate-900/40">
                    <td colSpan={6} className="border-b border-border px-3 py-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          aria-label={`${collapsedEntityIds.has(entity.id) ? 'Expand' : 'Collapse'} ${entity.entityType}`}
                          aria-expanded={!collapsedEntityIds.has(entity.id)}
                          onClick={() => setCollapsedEntityIds((ids) => {
                            const next = new Set(ids);
                            if (next.has(entity.id)) next.delete(entity.id); else next.add(entity.id);
                            return next;
                          })}
                          className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                        >
                          {collapsedEntityIds.has(entity.id) ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        </button>
                        <span className="font-semibold text-foreground">{entity.entityType}</span>
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{entity.fields.length} fields</span>
                        <button
                          type="button"
                          onClick={() => addField(entity.id)}
                          className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
                        >
                          <Plus className="h-3 w-3" /> Field
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Delete ${entity.entityType} and all of its fields?`)) {
                              removeEntity(entity.id);
                              toast.success('Row removed. Use Ctrl/Cmd+Z to undo.');
                            }
                          }}
                          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30 dark:hover:text-red-300"
                          title={`Delete ${entity.entityType}`}
                        >
                          <Trash2 className="h-3 w-3" /> Delete row
                        </button>
                      </div>
                    </td>
                  </tr>
                  {(!collapsedEntityIds.has(entity.id) || tableQuery.trim() !== '') && entity.fields.map((field, index) => {
                    const role = detectFieldRole(field.status, field.value);
                    const display = shownValue(field.value, field.status);
                    const hasError = field.isValid === false;
                    return (
                      <tr key={field.id} className="group hover:bg-sky-50/50 dark:hover:bg-sky-950/15">
                        <td className="sticky left-0 z-10 border-b border-r border-border bg-card px-3 py-1.5 font-medium text-muted-foreground group-hover:bg-sky-50/80 dark:group-hover:bg-sky-950/25">
                          {index === 0 ? entity.entityType : ''}
                        </td>
                        <td className="sticky left-52 z-10 border-b border-r border-border bg-card px-2 py-1.5 group-hover:bg-sky-50/80 dark:group-hover:bg-sky-950/25">
                          <div className="flex items-center gap-1">
                            <input
                              key={`${field.id}-name-${field.name}`}
                              defaultValue={field.name}
                              aria-label={`${entity.entityType} field name`}
                              onBlur={(event) => updateField(entity.id, field.id, { name: event.target.value.trim() || field.name })}
                              onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
                              className="h-7 min-w-0 flex-1 rounded border border-transparent bg-transparent px-1.5 font-medium outline-none hover:border-border focus:border-primary focus:bg-background focus:ring-2 focus:ring-primary/15"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm(`Delete ${entity.entityType}.${field.name}?`)) {
                                  removeField(entity.id, field.id);
                                  toast.success('Field removed. Use Ctrl/Cmd+Z to undo.');
                                }
                              }}
                              className="rounded p-1 text-muted-foreground opacity-0 transition-colors hover:bg-red-50 hover:text-red-700 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-red-950/30 dark:hover:text-red-300"
                              aria-label={`Delete ${entity.entityType}.${field.name}`}
                              title={`Delete ${field.name}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                        <td className="border-b border-r border-border px-2 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <input
                              key={`${field.id}-value-${display}`}
                              defaultValue={display}
                              aria-label={`${entity.entityType}.${field.name} value or formula`}
                              data-spreadsheet-value-cell
                              spellCheck={false}
                              title={isFormula(display) ? display : undefined}
                              onPaste={(event) => {
                                if (/[\t\r\n]/.test(event.clipboardData.getData('text'))) {
                                  event.preventDefault();
                                  toast.info('For multiple cells, use Import Excel / CSV so the column structure is preserved.');
                                }
                              }}
                              onFocus={() => { if (isFormula(display)) setExpandedFormulaId(field.id); }}
                              onBlur={(event) => { commitValue(entity.id, field.id, event.target.value); setExpandedFormulaId(null); }}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  if (!focusValueCell(event.currentTarget)) event.currentTarget.blur();
                                }
                                if (event.key === 'Tab') {
                                  const moved = focusValueCell(event.currentTarget, event.shiftKey ? -1 : 1);
                                  if (moved) event.preventDefault();
                                }
                                if (event.key === 'Escape') event.currentTarget.value = display;
                              }}
                              className={`h-7 min-w-0 flex-1 rounded border bg-background px-2 font-mono text-[11px] outline-none transition ${hasError ? 'border-red-400 text-red-700 focus:ring-red-300 dark:text-red-300' : 'border-transparent hover:border-border focus:border-primary focus:ring-2 focus:ring-primary/15'}`}
                            />
                            {hasError && <span role="img" aria-label={`Invalid references: ${field.invalidRefs?.join(', ') || 'unknown reference'}`} title={`Missing reference: ${field.invalidRefs?.join(', ') || 'unknown reference'}`}><AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-500" /></span>}
                          </div>
                          {expandedFormulaId === field.id && isFormula(display) && <p className="mt-1 rounded border border-sky-200 bg-sky-50 px-2 py-1 font-mono text-[10px] break-all text-sky-900 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-100">{display}</p>}
                          {hasError && field.invalidRefs && field.invalidRefs.length > 0 && <p className="mt-0.5 px-2 text-[10px] text-red-600 dark:text-red-300">Missing: {field.invalidRefs.join(', ')}</p>}
                        </td>
                        <td className="border-b border-r border-border px-3 py-1.5">
                          <div className="flex flex-wrap items-center gap-1">
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${roleStyle(role)}`}>{role}</span>
                            {field.blockedExcelFormula && <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200" title={`Blocked formula: =${field.blockedExcelFormula}`}>blocked Excel formula</span>}
                            {field.importedIsDate && <span className="rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[9px] font-medium text-violet-700 dark:border-violet-900 dark:bg-violet-950/30 dark:text-violet-300" title={field.importedDisplayValue ? `Excel display: ${field.importedDisplayValue}` : 'Imported Excel date'}>date</span>}
                          </div>
                        </td>
                        <td className="border-b border-r border-border px-2 py-1.5">
                          <select
                            value={field.formatUnit ?? 'none'}
                            aria-label={`${entity.entityType}.${field.name} unit`}
                            onChange={(event) => updateField(entity.id, field.id, { formatUnit: event.target.value as typeof UNITS[number] })}
                            className="h-7 w-full rounded border border-transparent bg-transparent px-1.5 text-[11px] outline-none hover:border-border focus:border-primary focus:bg-background"
                          >
                            {UNITS.map((unit) => <option key={unit} value={unit}>{unit === 'none' ? '—' : unit}</option>)}
                          </select>
                        </td>
                        <td className="border-b border-border px-3 py-1.5 font-mono text-[11px] text-emerald-700 dark:text-emerald-300">
                          {role === 'formula' && field.browserFormulaValue !== undefined && field.browserFormulaValue !== null
                            ? <span className="font-sans text-[10px] text-emerald-700 dark:text-emerald-300" title="Calculated in the browser with the Excel-compatible formula engine.">Calculated here: {String(field.browserFormulaValue)}</span>
                            : showSolution && field.solvedValue !== undefined && field.solvedValue !== null
                              ? String(field.solvedValue)
                            : role === 'formula' && field.importedCacheStale
                              ? <span className="font-sans text-[10px] text-amber-700 dark:text-amber-300" title="An input changed after this cache was read. Recalculate after exporting to Excel.">Needs Excel recalc</span>
                            : role === 'formula' && field.importedValue !== undefined && field.importedValue !== null
                              ? <span className="font-sans text-[10px] text-sky-700 dark:text-sky-300" title={field.importedFormulaCalculated ? 'Recalculated in the browser while importing this workbook.' : 'Cached value read from the source workbook; EquaFlow has not recalculated it.'}>{field.importedFormulaCalculated ? 'Calculated here' : 'Last Excel'}: {field.importedFormulaCalculated ? String(field.importedValue) : field.importedDisplayValue ?? String(field.importedValue)}</span>
                              : role === 'formula' && requiresExcelEvaluation(String(field.value))
                                ? <span className="font-sans text-[10px] text-amber-700 dark:text-amber-300" title="This formula is retained for export, but has not been evaluated here.">Not evaluated — Excel</span>
                              : <span className="text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
              {visibleEntityList.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">No fields match this search or filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border bg-muted/20 px-4 py-2 text-[10px] text-muted-foreground">
          <span><kbd className="rounded border bg-background px-1 py-0.5 font-mono">Enter</kbd> saves and moves to the next value</span>
          <span><kbd className="rounded border bg-background px-1 py-0.5 font-mono">=SUM(…)</kbd> recalculates here when compatible; native formulas stay in their source sheet on export</span>
          <span>EquaFlow exports include a hidden round-trip sheet.</span>
          <span className="basis-full sm:ml-auto sm:basis-auto">Drop an XLSX, XLS, XLSB, ODS, CSV, TSV, or TXT file anywhere here.</span>
          <span className="lg:hidden">Scroll the table horizontally to see values and results.</span>
        </div>
      </div>

      <Dialog open={pendingImport !== null} onOpenChange={(open) => { if (!open) { setPendingImport(null); setPendingFile(null); setNormalizationPlan(null); } }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Review spreadsheet import</DialogTitle>
            <DialogDescription>
              {pendingFileName} will replace the current model only after you confirm.
            </DialogDescription>
          </DialogHeader>

          {pendingImport && (
            <div className="space-y-3 text-xs">
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                {pendingImport.source === 'equaflow' ? (
                  <p className="font-medium text-emerald-700 dark:text-emerald-300">This is an EquaFlow export: its hidden round-trip snapshot will restore exactly.</p>
                ) : (
                  <p>Headers become field names; each populated row becomes a model block. Compatible Excel formulas are recalculated here, while native formulas remain in their source sheet for export.</p>
                )}
              </div>
              {pendingImport.source === 'spreadsheet' && pendingImport.preview?.schema.requiresNormalization && !normalizationPlan && (
                <div className="rounded-lg border border-violet-200 bg-violet-50/70 p-3 dark:border-violet-900/60 dark:bg-violet-950/20">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-violet-950 dark:text-violet-100">Structure needs review</p>
                      <p className="mt-0.5 text-[11px] text-violet-900/75 dark:text-violet-200/75">Local repair handles ordinary headers. The optional normalizer only receives redacted structure: dimensions, type counts, and layout flags — never this file, sheet names, headers, values, or formulas.</p>
                    </div>
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => void requestNormalization()} disabled={isImporting || isNormalizing}>
                      {isNormalizing ? 'Normalizing…' : 'Ask normalizer'}
                    </Button>
                  </div>
                </div>
              )}
              {pendingImport.source === 'spreadsheet' && (pendingImport.preview?.formulas ?? 0) > 0 && (
                <div className="rounded-lg border border-sky-200 bg-sky-50/70 p-3 dark:border-sky-900/60 dark:bg-sky-950/20">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-sky-950 dark:text-sky-100">Need a full Office recalculation?</p>
                      <p className="mt-0.5 text-[11px] text-sky-900/75 dark:text-sky-200/75">This explicitly uploads the selected workbook to EquaFlow&apos;s server for a disposable LibreOffice Calc run, then replaces it with the recalculated XLSX. It is never sent to the AI normalizer.</p>
                    </div>
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => void requestOfficeRecalculation()} disabled={isImporting || isNormalizing || isOfficeRecalculating}>
                      {isOfficeRecalculating ? 'Recalculating…' : 'Recalculate with Office'}
                    </Button>
                  </div>
                </div>
              )}
              {normalizationPlan && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-2 text-[11px] text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:text-emerald-100">
                  <p className="font-medium">Approved structural plan applied locally.</p>
                  {normalizationPlan.notes.map((note, index) => <p key={`${note}-${index}`} className="mt-0.5">{note}</p>)}
                </div>
              )}
              {/\.(csv|tsv|txt)$/i.test(pendingFileName) && pendingImport.source === 'spreadsheet' && (
                <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-2 text-xs">
                  <span><strong>CSV text encoding</strong><span className="ml-1 text-muted-foreground">Change this if names look garbled.</span></span>
                  <select value={textEncoding} onChange={(event) => void changeTextEncoding(event.target.value as 'utf-8' | 'windows-1251')} disabled={isImporting} className="h-7 rounded border border-border bg-background px-1.5 text-xs">
                    <option value="utf-8">UTF-8</option>
                    <option value="windows-1251">Windows-1251</option>
                  </select>
                </label>
              )}
              <div className="grid gap-2 sm:grid-cols-3">
                <div className="rounded-md border border-border p-2"><span className="block text-muted-foreground">Sheets</span><strong>{pendingImport.preview?.sheets.length ?? 0}</strong></div>
                <div className="rounded-md border border-border p-2"><span className="block text-muted-foreground">Preview blocks</span><strong>{pendingImport.project.entities.length}</strong></div>
                <div className="rounded-md border border-border p-2"><span className="block text-muted-foreground">Excel formulas</span><strong>{pendingImport.preview?.formulas ?? 0}</strong></div>
              </div>
              <div className="max-h-44 space-y-2 overflow-auto rounded-lg border border-border p-2">
                {pendingImport.preview?.sheets.map((sheet) => (
                  <label key={sheet.name} className={`block rounded-md px-2.5 py-2 ${selectedSheetNames.includes(sheet.name) || pendingImport.source === 'equaflow' ? 'bg-muted/40' : 'bg-muted/15 opacity-60'}`}>
                    <div className="flex justify-between gap-3 font-medium">
                      <span className="flex min-w-0 items-center gap-2">
                        {pendingImport.source === 'spreadsheet' && <input type="checkbox" checked={selectedSheetNames.includes(sheet.name)} onChange={() => setSelectedSheetNames((names) => names.includes(sheet.name) ? names.filter((name) => name !== sheet.name) : [...names, sheet.name])} />}
                        <span className="truncate">{sheet.name}</span>
                      </span>
                      <span className={`shrink-0 ${sheet.importableRows > MAX_IMPORT_ROWS_PER_SHEET ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>{sheet.rows} rows · {sheet.columns} columns</span>
                    </div>
                    {sheet.headers.length > 0 && <p className="mt-1 truncate text-[11px] text-muted-foreground">Headers: {sheet.headers.join(' · ')}</p>}
                    {sheet.schema.issues.length > 0 && <p className={`mt-1 text-[10px] ${sheet.schema.confidence === 'needs_normalization' ? 'text-violet-700 dark:text-violet-300' : 'text-muted-foreground'}`}>Schema: {sheet.schema.issues.map((issue) => issue.replace(/_/g, ' ')).join(' · ')}</p>}
                    {sheet.sampleRows.length > 0 && <div className="mt-1.5 space-y-0.5 border-l border-border pl-2 font-mono text-[10px] text-muted-foreground">{sheet.sampleRows.map((row, index) => <p key={index} className="truncate">{row.join(' · ') || '—'}</p>)}</div>}
                    {sheet.importableRows > MAX_IMPORT_ROWS_PER_SHEET && <p className="mt-1 text-[10px] text-amber-700 dark:text-amber-300">{sheet.importableRows} populated records: deselect this sheet or split it into batches of {MAX_IMPORT_ROWS_PER_SHEET} before importing it as a visual model.</p>}
                  </label>
                ))}
              </div>
              {pendingImport.warnings.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                  {pendingImport.warnings.map((warning) => <p key={warning}>{warning}</p>)}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setPendingImport(null); setPendingFile(null); setNormalizationPlan(null); }}>Cancel</Button>
            <Button onClick={confirmImport} disabled={isImporting || isNormalizing || isOfficeRecalculating || (pendingImport?.source === 'spreadsheet' && (selectedSheetNames.length === 0 || hasSelectedOversizedSheet))} title={hasSelectedOversizedSheet ? `Split selected sheets into batches of ${MAX_IMPORT_ROWS_PER_SHEET} records before importing.` : undefined}>{isImporting ? 'Importing…' : 'Import and replace'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
